<template>
  <div>
    <header class="site-header" :class="{ scrolled: compactHeader }">
      <div class="section-container header-inner">
      <div class="header-left" @click="goHome">
        <img :src="headerLogo" alt="logo" class="brand-logo" />
      </div>
      <div v-if="!isMobile" class="header-center">
        <nav class="header-nav" aria-label="主导航">
          <template v-for="item in navItems">
            <a-dropdown
              v-if="isProductsNavItem(item)"
              :key="item.key"
              :trigger="['hover']"
              placement="bottomCenter"
              overlayClassName="product-menu-dropdown"
              :getPopupContainer="dropdownGetPopupContainer"
            >
              <span
                class="header-nav__link header-nav__link--products"
                :class="{ 'header-nav__link--active': isNavActive(item) }"
                role="button"
                tabindex="0"
                @click.prevent
                @keydown.enter.prevent
              >
                {{ item.label }}
                <a-icon type="down" class="header-nav__caret" aria-hidden="true" />
              </span>
              <div slot="overlay" class="product-menu-overlay">
                <div class="product-menu-overlay__body product-menu-overlay__body--cols">
                  <template v-if="productMenuItems.length">
                    <div class="product-menu-col">
                      <p class="product-menu-col__title">B端产品</p>
                      <div class="product-menu-col__list">
                        <router-link
                          v-for="p in productMenuListB"
                          :key="p.id"
                          class="product-menu-card"
                          :to="productDetailRoute(p) || { name: 'productDetail', params: { slug: 'oms' } }"
                        >
                          <div class="product-menu-card__icon-wrap">
                            <img
                              v-if="p.icon"
                              :src="p.icon"
                              alt=""
                              class="product-menu-card__icon"
                            />
                            <div v-else class="product-menu-card__icon product-menu-card__icon--ph" />
                          </div>
                          <div class="product-menu-card__text">
                            <span class="product-menu-card__name">{{ p.name }}</span>
                            <span class="product-menu-card__desc">{{ shortDescForProduct(p) }}</span>
                          </div>
                        </router-link>
                        <p v-if="!productMenuListB.length" class="product-menu-col__empty">暂无</p>
                      </div>
                    </div>
                    <div class="product-menu-col product-menu-col--split">
                      <p class="product-menu-col__title">C端产品</p>
                      <div class="product-menu-col__list">
                        <router-link
                          v-for="p in productMenuListC"
                          :key="p.id"
                          class="product-menu-card"
                          :to="productDetailRoute(p) || { name: 'productDetail', params: { slug: 'oms' } }"
                        >
                          <div class="product-menu-card__icon-wrap">
                            <img
                              v-if="p.icon"
                              :src="p.icon"
                              alt=""
                              class="product-menu-card__icon"
                            />
                            <div v-else class="product-menu-card__icon product-menu-card__icon--ph" />
                          </div>
                          <div class="product-menu-card__text">
                            <span class="product-menu-card__name">{{ p.name }}</span>
                            <span class="product-menu-card__desc">{{ shortDescForProduct(p) }}</span>
                          </div>
                        </router-link>
                        <p v-if="!productMenuListC.length" class="product-menu-col__empty">暂无</p>
                      </div>
                    </div>
                  </template>
                  <p v-else class="product-menu-empty">暂无产品数据</p>
                </div>
              </div>
            </a-dropdown>
            <span
              v-else
              :key="item.key"
              class="header-nav__link"
              :class="{ 'header-nav__link--active': isNavActive(item) }"
              role="button"
              tabindex="0"
              @click="navigate(item.key)"
              @keydown.enter.prevent="navigate(item.key)"
            >
              {{ item.label }}
            </span>
          </template>
        </nav>
      </div>
      <div class="header-right">
        <div class="lang-select-wrap">
          <a-icon type="global" class="lang-prefix-icon" />
          <a-select
            class="lang-select"
            size="small"
            :value="$i18n.locale"
            style="width: 98px"
            dropdownClassName="lang-select-dropdown"
            @change="$emit('change-lang', $event)"
          >
            <a-select-option value="zh">中文</a-select-option>
            <a-select-option value="en">EN</a-select-option>
            <a-select-option value="th">ไทย</a-select-option>
          </a-select>
        </div>
        <template v-if="!isMobile">
          <a-button class="cta-btn" type="primary" @click="openDemoModal()">
            {{ $t("trial") }}
          </a-button>
        </template>
        <a-button
          v-else
          class="mobile-menu-trigger"
          shape="circle"
          @click="$emit('update-mobile-drawer', true)"
        >
          <a-icon type="menu" />
        </a-button>
      </div>
      </div>
    </header>

    <a-drawer
      :visible="mobileDrawerVisible"
      placement="right"
      :width="300"
      wrapClassName="mobile-nav-drawer"
      :body-style="{ padding: '18px 16px calc(20px + env(safe-area-inset-bottom))' }"
      @close="$emit('update-mobile-drawer', false)"
    >
      <div class="mobile-drawer-content">
        <div class="mobile-drawer-head">
          <p class="mobile-drawer-title">导航菜单</p>
          <p class="mobile-drawer-desc">进入各页面或返回首页锚点</p>
        </div>
        <a-space direction="vertical" class="mobile-nav-list">
          <a-button class="mobile-nav-btn" block @click="navigate('/')">首页</a-button>
          <a-button class="mobile-nav-btn" block @click="goHomeHash('intro')">公司简介</a-button>
          <a-button class="mobile-nav-btn" block @click="goHomeHash('features')">核心功能</a-button>
          <a-button class="mobile-nav-btn" block @click="goHomeHash('values')">价值</a-button>
          <a-button class="mobile-nav-btn" block @click="goHomeHash('audience')">适用客户</a-button>
          <div class="mobile-drawer-sub">
            <p class="mobile-drawer-subtitle">B端产品</p>
            <a-button
              v-for="p in productMenuListB"
              :key="p.id"
              class="mobile-nav-btn mobile-nav-btn--sub"
              block
              @click="goProductDetail(p)"
            >
              {{ p.name }}
            </a-button>
            <p class="mobile-drawer-subtitle mobile-drawer-subtitle--second">C端产品</p>
            <a-button
              v-for="p in productMenuListC"
              :key="p.id"
              class="mobile-nav-btn mobile-nav-btn--sub"
              block
              @click="goProductDetail(p)"
            >
              {{ p.name }}
            </a-button>
          </div>
          <a-button class="mobile-nav-btn" block @click="navigate('/cases')">案例</a-button>
          <a-button class="mobile-nav-btn" block @click="navigate(helpCenterLoginUrl)">帮助中心</a-button>
          <a-button class="mobile-nav-btn" block @click="goHomeHash('partners')">伙伴</a-button>
          <a-button class="mobile-nav-btn" block @click="goHomeHash('contact')">联系</a-button>
        </a-space>
        <div class="mobile-drawer-footer">
          <a-button block type="primary" class="mobile-trial-btn" @click="openDemoModal(true)">
            {{ $t("trial") }}
          </a-button>
        </div>
      </div>
    </a-drawer>
    <demo-register-modal :visible="demoModalVisible" @update:visible="demoModalVisible = $event" />
  </div>
</template>

<script>
import headerLogo from "@/static/logo.png";
import { HELP_CENTER_LOGIN_URL } from "@/apps/official/constants";
import { PRODUCT_SLUGS } from "@/mock/defaultData";
import DemoRegisterModal from "@/apps/official/components/DemoRegisterModal.vue";

export default {
  components: {
    DemoRegisterModal
  },
  data() {
    return {
      headerLogo,
      helpCenterLoginUrl: HELP_CENTER_LOGIN_URL,
      demoModalVisible: false
    };
  },
  props: {
    basicInfo: {
      type: Object,
      default: () => ({})
    },
    navItems: {
      type: Array,
      default: () => []
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    compactHeader: {
      type: Boolean,
      default: false
    },
    mobileDrawerVisible: {
      type: Boolean,
      default: false
    },
    products: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    productMenuItems() {
      const list = this.products || [];
      return list.filter((p) => this.productSlugFromId(p && p.id));
    },
    productMenuListB() {
      return this.productMenuItems.filter((p) => !this.isProductAudienceC(p));
    },
    productMenuListC() {
      return this.productMenuItems.filter((p) => this.isProductAudienceC(p));
    }
  },
  methods: {
    dropdownGetPopupContainer() {
      return document.body;
    },
    /** 接口可传 audience: c；无则按 prd-bi 视为 C 端 */
    isProductAudienceC(product) {
      const raw = product && product.audience != null ? String(product.audience).trim().toLowerCase() : "";
      if (raw === "c" || raw === "c端" || raw === "consumer") return true;
      if (raw === "b" || raw === "b端" || raw === "business") return false;
      const id = String((product && product.id) || "");
      const m = id.match(/^prd-([a-z0-9]+)$/i);
      return m && m[1].toLowerCase() === "bi";
    },
    isProductsNavItem(item) {
      const k = item && item.key ? String(item.key) : "";
      return k.startsWith("/products");
    },
    isNavActive(item) {
      const p = this.$route.path || "/";
      const key = item.key;
      if (!key || /^https?:\/\//i.test(key)) return false;
      if (this.isProductsNavItem(item)) {
        return p === "/products" || p.startsWith("/products/");
      }
      if (key === p) return true;
      if (key !== "/" && p.startsWith(`${key}/`)) return true;
      return false;
    },
    productSlugFromId(id) {
      const m = String(id || "").match(/^prd-([a-z0-9]+)$/i);
      return m && PRODUCT_SLUGS.includes(m[1].toLowerCase()) ? m[1].toLowerCase() : "";
    },
    productDetailRoute(product) {
      const slug = this.productSlugFromId(product && product.id);
      return slug ? { name: "productDetail", params: { slug } } : null;
    },
    shortDescForProduct(item) {
      const maxLen = 36;
      let base;
      if (item.subtitle && String(item.subtitle).trim()) {
        base = String(item.subtitle).trim();
      } else {
        const intro = (item.intro || "").trim();
        base = intro || "一体化管理与高效协同。";
      }
      const first = base.split(/\r?\n/)[0].replace(/\s+/g, " ");
      return first.length > maxLen ? `${first.slice(0, maxLen)}…` : first;
    },
    normalizePath(path) {
      if (!path || path === "/") return "/";
      return path.startsWith("/") ? path : `/${path}`;
    },
    navigate(path) {
      if (/^https?:\/\//i.test(path)) {
        window.open(path, "_blank", "noopener,noreferrer");
        this.$emit("update-mobile-drawer", false);
        return;
      }
      const target = this.normalizePath(path);
      if (this.$route.path !== target) {
        this.$router.push(target);
      }
      this.$emit("update-mobile-drawer", false);
    },
    goHome() {
      if (this.$route.path !== "/") {
        this.$router.push("/");
      }
    },
    goHomeHash(anchor) {
      if (this.$route.path !== "/") {
        this.$router.push({ path: "/", hash: `#${anchor}` });
      } else {
        this.$router.replace({ hash: `#${anchor}` });
        this.$nextTick(() => {
          const el = document.getElementById(anchor);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      }
      this.$emit("update-mobile-drawer", false);
    },
    goProductDetail(product) {
      const route = this.productDetailRoute(product);
      if (route) {
        this.$router.push(route);
      } else {
        this.$router.push({ name: "productDetail", params: { slug: "oms" } });
      }
      this.$emit("update-mobile-drawer", false);
    },
    openDemoModal(closeDrawer) {
      if (closeDrawer) {
        this.$emit("update-mobile-drawer", false);
      }
      this.demoModalVisible = true;
    }
  }
};
</script>

<style scoped>
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--header-h);
  transition: background 0.35s, box-shadow 0.35s, backdrop-filter 0.35s;
}

.site-header.scrolled {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  box-shadow: 0 1px 0 var(--border);
}

.site-header:not(.scrolled) {
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
}

.header-inner {
  display: flex;
  align-items: center;
  height: var(--header-h);
  gap: 24px;
}

.header-left {
  width: 340px;
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}

.brand-logo {
  width: 160px;
  height: 160px;
  object-fit: contain;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.header-left:hover .brand-logo {
  transform: translateY(-2px) scale(1.02);
}

.header-center {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

.header-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.lang-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.lang-prefix-icon {
  position: absolute;
  left: 10px;
  color: var(--text-muted);
  font-size: 13px;
  z-index: 1;
  pointer-events: none;
}

.site-header /deep/ .lang-select .ant-select-selection {
  height: 30px;
  line-height: 28px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-soft);
  box-shadow: none;
  transition: all 0.25s ease;
}

.site-header /deep/ .lang-select .ant-select-selection:hover {
  border-color: rgba(22, 119, 255, 0.35);
}

.site-header /deep/ .lang-select .ant-select-selection-selected-value {
  color: var(--text);
  font-weight: 500;
  padding-left: 18px;
}

.site-header /deep/ .lang-select .ant-select-arrow {
  color: var(--text-muted);
}

.site-header /deep/ .cta-btn {
  height: 38px;
  padding: 0 18px;
  font-size: 14px;
  border-radius: 8px;
  background: var(--primary) !important;
  border-color: var(--primary) !important;
  color: #fff !important;
}

.site-header /deep/ .cta-btn:hover,
.site-header /deep/ .cta-btn:focus {
  background: var(--primary-light) !important;
  border-color: var(--primary-light) !important;
}

.header-nav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 4px 8px;
}

.site-header /deep/ .header-nav .ant-dropdown {
  display: inline-flex;
  align-items: stretch;
}

.header-nav__link {
  display: inline-block;
  padding: 0 12px;
  height: 40px;
  line-height: 40px;
  color: var(--text-muted);
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.25s;
  position: relative;
  user-select: none;
}

.header-nav__link::after {
  content: "";
  position: absolute;
  bottom: 4px;
  left: 12px;
  right: 12px;
  height: 2px;
  background: var(--primary);
  border-radius: 1px;
  transform: scaleX(0);
  transition: transform 0.3s;
}

.header-nav__link:hover,
.header-nav__link:focus {
  outline: none;
  color: var(--primary);
}

.header-nav__link--active {
  color: var(--primary);
}

.header-nav__link--active::after {
  transform: scaleX(1);
}

.header-nav__caret {
  font-size: 10px;
  margin-left: 2px;
  opacity: 0.88;
  vertical-align: middle;
}

.site-header /deep/ .mobile-menu-trigger {
  width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  background: var(--bg-soft);
  color: var(--text);
}

.site-header /deep/ .mobile-menu-trigger:hover,
.site-header /deep/ .mobile-menu-trigger:focus {
  border-color: rgba(22, 119, 255, 0.35);
  color: var(--primary);
}

.mobile-drawer-content {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.mobile-drawer-head {
  margin-bottom: 14px;
}

.mobile-drawer-title {
  margin: 0;
  color: var(--text);
  font-size: 18px;
  font-weight: 600;
}

.mobile-drawer-desc {
  margin: 6px 0 0;
  color: var(--text-muted);
  font-size: 12px;
}

.mobile-drawer-sub {
  margin: 10px 0 6px;
  padding: 10px 0 4px;
  border-top: 1px solid var(--border);
}

.mobile-drawer-subtitle {
  margin: 0 0 8px;
  padding-left: 2px;
  font-size: 12px;
  font-weight: 600;
  color: var(--primary);
  letter-spacing: 0.04em;
}

.mobile-drawer-subtitle--second {
  margin-top: 14px;
}

.mobile-nav-btn--sub {
  height: 40px;
  margin-bottom: 6px;
  font-size: 13px;
}

.mobile-nav-list {
  width: 100%;
}

.mobile-nav-list /deep/ .ant-space-item {
  width: 100%;
}

.mobile-nav-btn {
  height: 44px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--bg-soft);
  color: var(--text);
  text-align: left;
  font-weight: 500;
  padding-left: 14px;
}

.mobile-nav-btn:hover,
.mobile-nav-btn:focus {
  color: var(--primary);
  border-color: rgba(22, 119, 255, 0.35);
  background: rgba(22, 119, 255, 0.06);
}

.mobile-drawer-footer {
  margin-top: auto;
  padding-top: 16px;
}

.mobile-trial-btn {
  height: 42px;
  border-radius: 10px;
}

.mobile-nav-drawer /deep/ .ant-drawer-content {
  background: var(--bg);
}

.mobile-nav-drawer /deep/ .ant-drawer-header {
  background: transparent;
  border-bottom: 1px solid var(--border);
}

.mobile-nav-drawer /deep/ .ant-drawer-title,
.mobile-nav-drawer /deep/ .ant-drawer-close {
  color: var(--text);
}

@media (max-width: 991px) {
  .header-right {
    gap: 6px;
  }

  .site-header /deep/ .lang-select {
    width: 92px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .header-left:hover .brand-logo {
    transform: none;
  }
}

@media (max-width: 991px) {
  .header-left {
    width: auto;
  }
}

@media (max-width: 575px) {
  .site-header /deep/ .lang-select {
    width: 82px !important;
  }

  .brand-logo {
    width: 128px;
    height: 128px;
  }
}
</style>

<style>
/* 产品下拉：浅色卡片风格 */
.product-menu-dropdown.ant-dropdown {
  z-index: 1050;
}

.product-menu-overlay {
  width: 720px;
  max-width: min(720px, calc(100vw - 24px));
  box-sizing: border-box;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: 0 20px 48px rgba(10, 39, 93, 0.12);
  overflow: hidden;
}

.product-menu-overlay__body {
  margin: 14px 0 16px;
  padding: 16px 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.product-menu-overlay__body--cols {
  flex-direction: row;
  align-items: stretch;
  gap: 0;
  padding: 12px 8px 16px;
}

.product-menu-col {
  flex: 1;
  min-width: 0;
  padding: 0 8px;
}

.product-menu-col--split {
  border-left: 1px solid var(--border);
}

.product-menu-col__title {
  display: inline-block;
  margin: 0 0 14px;
  padding: 0 4px 10px 2px;
  max-width: 100%;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.02em;
  color: var(--primary-dark);
  border-bottom: 1px solid var(--border);
}

.product-menu-col__list {
  display: flex;
  flex-direction: column;
}

.product-menu-col__empty {
  margin: 0;
  padding: 20px 8px 16px;
  font-size: 12px;
  color: var(--text-muted);
  text-align: center;
}

.product-menu-card {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 0;
  padding: 12px 8px;
  border-radius: 8px;
  border: none;
  border-bottom: 1px solid var(--border);
  background: transparent;
  color: inherit;
  text-decoration: none;
  transition: background 0.2s ease;
}

.product-menu-col__list > .product-menu-card:last-child {
  border-bottom: none;
}

.product-menu-card:hover {
  background: rgba(22, 119, 255, 0.06);
}

.product-menu-card__icon-wrap {
  flex-shrink: 0;
}

.product-menu-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: contain;
  display: block;
  box-shadow: var(--shadow);
  background: #fff;
}

.product-menu-card__icon--ph {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: linear-gradient(145deg, rgba(22, 119, 255, 0.15), rgba(10, 39, 93, 0.1));
  border: 1px solid var(--border);
}

.product-menu-card__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.product-menu-card__name {
  color: var(--text);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
}

.product-menu-card__desc {
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-menu-empty {
  margin: 0;
  padding: 32px 16px 28px;
  text-align: center;
  color: var(--text-muted);
  font-size: 13px;
}
</style>
